<template>
  <button
    type="button"
    class="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 transition-colors"
    :class="[variantClass, { 'opacity-50 cursor-not-allowed': disabled }]"
    role="menuitem"
    :disabled="disabled"
    @click.stop="handleClick"
  >
    <!-- Loading Spinner -->
    <LoadingSpinner
      v-if="isLoading"
      :style="spinnerStyle"
      :color="spinnerColor"
      :show-text="false"
      container-class=""
    />
    <!-- Static Icon -->
    <component
      :is="icon"
      v-else-if="icon"
      class="h-4 w-4"
    />
    <slot />
  </button>
</template>

<script setup lang="ts">
import { computed, inject } from 'vue';
import type { Component } from 'vue';
import LoadingSpinner from '@/components/base/LoadingSpinner.vue';

interface Props {
  icon?: Component;
  variant?: 'default' | 'primary' | 'success' | 'warning' | 'danger' | 'info';
  disabled?: boolean;
  isLoading?: boolean;
  spinnerStyle?: 'circle' | 'arrow';
  spinnerColor?: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'slate';
}

const props = withDefaults(defineProps<Props>(), {
  icon: undefined,
  variant: 'default',
  disabled: false,
  isLoading: false,
  spinnerStyle: 'arrow',
  spinnerColor: 'primary'
});

const emit = defineEmits<{
  click: [];
}>();

// Inject closeDropdown function from parent DropdownMenu
const closeDropdown = inject<(() => void) | null>('closeDropdown', null);

const variantClass = computed(() => {
  const variants = {
    default: 'text-gray-700 dark:text-gray-300',
    primary: 'text-indigo-600 dark:text-indigo-400',
    success: 'text-green-600 dark:text-green-400',
    warning: 'text-amber-600 dark:text-amber-400',
    danger: 'text-red-600 dark:text-red-400',
    info: 'text-blue-600 dark:text-blue-400'
  };
  return variants[props.variant];
});

const handleClick = () => {
  if (!props.disabled) {
    emit('click');
    // Close the dropdown after the click event
    if (closeDropdown) {
      closeDropdown();
    }
  }
};
</script>
